import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import { Box, Chip, Divider, FormControl, FormHelperText, List, ListItem } from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO } from "date-fns";

/**
 * Shared date-range picker for waste pages. Encapsulates the `DateRangePicker`
 * styling, the historical shortcut chips (this/last two years, last/this
 * quarter, reset), and ISO string ↔ Date conversion so consumers only deal with
 * `YYYY-MM-DD` strings.
 *
 * Used by the tenant Waste Streams page and the per-facility waste filters.
 * Other waste date pickers (overview, submissions, fulfillment) keep their own
 * because their shortcut sets differ — notably submissions/fulfillment use
 * forward-looking windows. Adopting them here would require making the shortcut
 * list a prop.
 */
type WasteDateRangePickerProps = {
  /** ISO YYYY-MM-DD or undefined. */
  startDate?: string;
  /** ISO YYYY-MM-DD or undefined. */
  endDate?: string;
  /**
   * Fires when the user commits a new range via OK or a shortcut chip.
   * Strings are ISO YYYY-MM-DD; either side can be undefined to clear.
   */
  onAccept: (next: { startDate?: string; endDate?: string }) => void;
  /** Label for the start input. Defaults to "Start Date". */
  startLabel?: string;
  /** Label for the end input. Defaults to "End Date". */
  endLabel?: string;
  /**
   * Show the "Select a start and end date to view data" helper text when
   * one side is missing. Defaults to true.
   */
  showHelperText?: boolean;
  /** Min width of the form control. Defaults to 300px. */
  minWidth?: number | string;
};

export function WasteDateRangePicker({
  startDate,
  endDate,
  onAccept,
  startLabel = "Start Date",
  endLabel = "End Date",
  showHelperText = true,
  minWidth = 300,
}: WasteDateRangePickerProps) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);

  const handleAccept = (next: [Date | null, Date | null]) => {
    onAccept({
      startDate: dateToIsoString(next[0]),
      endDate: dateToIsoString(next[1]),
    });
  };

  const helperText =
    showHelperText && (!start || !end) ? "Select a start and end date to view data" : null;

  return (
    <FormControl sx={{ minWidth }} size="small" variant="standard">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateRangePicker
          calendars={2}
          onAccept={handleAccept}
          value={[start, end]}
          localeText={{ start: startLabel, end: endLabel }}
          slots={{
            field: MultiInputDateRangeField,
            shortcuts: HorizontalShortcuts,
          }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              variant: "standard",
              sx: { "& .MuiInputBase-root": { height: "40px" } },
              inputProps: {
                autoComplete: "off",
                "data-1p-ignore": "true",
                "data-lpignore": "true",
                "data-form-type": "other",
              },
              InputProps: { endAdornment: <InsertInvitationIcon /> },
            },
            shortcuts: { items: datePickerShortcuts },
          }}
        />
      </LocalizationProvider>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

function parseIsoDate(iso?: string): Date | null {
  if (!iso) return null;
  const d = parseISO(iso);
  return isNaN(d.getTime()) ? null : d;
}

function dateToIsoString(d: Date | null): string | undefined {
  if (!d || isNaN(d.getTime())) return undefined;
  try {
    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
}

const datePickerShortcuts = [
  {
    // Current year, but only through the end of the current month — matches
    // WasteFacilityFilters' shortcut. Avoids implying "we have full-year data"
    // mid-year, which Dec 31 would.
    label: `${new Date().getFullYear()}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), 0, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return [start, end] as [Date, Date];
    },
  },
  {
    label: `${new Date().getFullYear() - 1}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return [start, end] as [Date, Date];
    },
  },
  {
    label: `${new Date().getFullYear() - 2}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 2, 0, 1);
      const end = new Date(today.getFullYear() - 2, 11, 31);
      return [start, end] as [Date, Date];
    },
  },
  {
    label: "Last Quarter",
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const lastQuarterYear = currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const start = new Date(lastQuarterYear, lastQuarter * 3, 1);
      const end = new Date(lastQuarterYear, (lastQuarter + 1) * 3, 0);
      return [start, end] as [Date, Date];
    },
  },
  {
    label: "This Quarter",
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const start = new Date(today.getFullYear(), currentQuarter * 3, 1);
      const end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
      return [start, end] as [Date, Date];
    },
  },
  {
    // Rolling trailing year: ends yesterday, starts the same date one year earlier.
    label: "Last 12 Months",
    getValue: () => {
      const today = new Date();
      const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      const start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate() - 1);
      return [start, end] as [Date, Date];
    },
  },
  {
    label: "Reset",
    getValue: (): [Date | null, Date | null] => [null, null],
  },
];

function HorizontalShortcuts(props: PickersShortcutsProps<DateRange<Date>>) {
  const { items, onChange, isValid, changeImportance = "accept" } = props;

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const newValue = item.getValue({ isValid });
    return {
      label: item.label,
      onClick: () => {
        onChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box sx={{ width: "100%", gridRow: 1, gridColumn: 2 }}>
      <List
        dense
        sx={(theme) => ({
          display: "flex",
          justifyContent: "center",
          px: theme.spacing(2),
          "& .MuiListItem-root": {
            width: "inherit",
            py: 0,
            px: theme.spacing(0.5),
          },
        })}
      >
        {resolvedItems.map((item) => (
          <ListItem key={item.label}>
            <Chip {...item} />
          </ListItem>
        ))}
      </List>
      <Divider />
    </Box>
  );
}
